import { baseApi } from './baseApi';

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  message?: string;
  status: 'new' | 'contacted' | 'qualified' | 'closed';
  source: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  customer: {
    id: string;
    name: string;
    email: string;
    company?: string;
  };
  landingPage?: {
    id: string;
    title: string;
    slug: string;
    template?: string;
  };
}

export interface LeadsResponse {
  success: boolean;
  data: {
    leads: Lead[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface LeadResponse {
  success: boolean;
  data: Lead;
}

export interface LeadStats {
  totalLeads: number;
  newLeads: number;
  contactedLeads: number;
  qualifiedLeads: number;
  closedLeads: number;
}

export interface LeadStatsResponse {
  success: boolean;
  data: LeadStats;
}

export interface CreateLeadRequest {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  source: string;
  landingPageId?: string;
  customerId?: string;
}

export interface UpdateLeadRequest {
  status?: 'new' | 'contacted' | 'qualified' | 'closed';
  notes?: string;
  phone?: string;
}

export interface LeadsFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'new' | 'contacted' | 'qualified' | 'closed';
  source?: string;
  customerId?: string;
  landingPageId?: string;
}

export const leadsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLeads: builder.query<LeadsResponse, LeadsFilters | void>({
      query: (filters = {}) => ({
        url: '/leads',
        params: {
          page: 1,
          limit: 20,
          ...filters,
        },
      }),
      providesTags: ['Lead'],
    }),

    getLead: builder.query<LeadResponse, string>({
      query: (id) => `/leads/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Lead', id }],
    }),

    getLeadStats: builder.query<LeadStatsResponse, { customerId?: string; landingPageId?: string } | void>({
      query: (filters) => ({
        url: '/leads/stats',
        params: filters || {},
      }),
      providesTags: ['LeadStats'],
    }),

    createLead: builder.mutation<LeadResponse, CreateLeadRequest>({
      query: (lead) => ({
        url: '/leads',
        method: 'POST',
        body: lead,
      }),
      invalidatesTags: ['Lead', 'LeadStats'],
    }),

    updateLead: builder.mutation<LeadResponse, { id: string; updates: UpdateLeadRequest }>({
      query: ({ id, updates }) => ({
        url: `/leads/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Lead', id },
        'Lead',
        'LeadStats',
      ],
    }),

    deleteLead: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/leads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Lead', 'LeadStats'],
    }),
  }),
});

export const {
  useGetLeadsQuery,
  useGetLeadQuery,
  useGetLeadStatsQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
} = leadsApi;